import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Smartphone, Calculator } from "lucide-react";
import PictureImg from "@/components/PictureImg";

// Renders "[label](url)" markdown links inside plain text (internal -> <Link>, external -> <a>)
function linkify(text = "") {
  text = String(text);
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      m[2].startsWith("/") ? (
        <Link key={m.index} to={m[2]} className="text-[var(--faro-blue)] font-bold underline underline-offset-2">{m[1]}</Link>
      ) : (
        <a key={m.index} href={m[2]} target="_blank" rel="noreferrer" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">{m[1]}</a>
      )
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function Block({ b, i }) {
  if (b.h) {
    return (
      <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight text-[var(--faro-blue)] pt-3">
        {b.h}
      </h2>
    );
  }
  if (b.list) {
    return (
      <ul className="space-y-2">
        {b.list.map((li, lii) => (
          <li key={`${lii}-${li.slice(0, 32)}`} className="flex items-start gap-3 text-lg">
            <span className="mt-2.5 w-2 h-2 rounded-full bg-[var(--faro-red)] shrink-0" />
            <span>{linkify(li)}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (b.table) {
    return (
      <div className="overflow-x-auto rounded-2xl border border-[rgba(30,42,143,0.12)]">
        <table className="w-full text-sm sm:text-base">
          <thead>
            <tr className="bg-[var(--faro-blue)] text-white text-left">
              {b.table.headers.map((h) => (
                <th key={h} className="px-4 py-3 font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {b.table.rows.map((row, ri) => (
              <tr key={`r-${ri}`} className="border-t border-[rgba(30,42,143,0.08)] even:bg-[var(--faro-bg)]">
                {row.map((cell, ci) => (
                  <td key={`c-${ri}-${ci}`} className={`px-4 py-3 ${ci === 0 ? "font-semibold text-[var(--faro-blue-deep)]" : ""}`}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (b.note) {
    return (
      <div className="rounded-2xl bg-[rgba(255,195,0,0.14)] border-l-4 border-[var(--faro-yellow)] p-5 flex items-start gap-3">
        <Smartphone size={22} className="text-[var(--faro-blue)] mt-0.5 shrink-0" />
        <p className="text-lg font-semibold text-[var(--faro-text)] m-0">{b.note}</p>
      </div>
    );
  }
  return <p className="text-lg">{linkify(b.p)}</p>;
}

const MediaFigure = ({ m, i }) => (
  <figure className="rounded-2xl overflow-hidden border border-[rgba(30,42,143,0.12)] bg-white" data-testid={`media-${i}`}>
      {m.type === "image" && (
        <img src={m.url} alt={m.caption || `Media ${i + 1}`} width="800" height="450" loading="lazy" className="w-full h-auto" />
      )}
      {m.type === "video" && (
        <video src={m.url} controls className="w-full h-auto" />
      )}
      {m.type === "embed" && (
        <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
          <iframe
            src={m.url}
            title={m.caption || `Embed ${i + 1}`}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}
      {m.caption && (
        <figcaption className="p-3 text-sm text-[var(--faro-text-soft)]">{m.caption}</figcaption>
      )}
    </figure>
);

// Contextual end-of-article CTA. Keyed by post slug/id → an article-specific offer.
// Falls back to the generic "Quiero información" block when no entry matches.
const POST_CTA = {
  "permiso-por-puntos-como-funciona": {
    title: "¿Ya sabes qué curso te toca? Reserva ahora",
    subtitle: "Impartimos convocatorias cada mes en el centro de Plaza América (Vigo). Reserva online en 2 minutos.",
    primary: { label: "Reserva tu curso", to: "/permiso-por-puntos-vigo", testid: "post-cta-primary" },
    secondary: { label: "Tengo claro que quiero el parcial (4 puntos)", to: "/recuperacion-de-puntos-vigo", testid: "post-cta-secondary" },
  },
};

const PostCta = ({ post }) => {
  const cfg = POST_CTA[post.slug] || POST_CTA[post.id];
  if (!cfg) {
    return (
      <div className="mt-12 rounded-2xl bg-[var(--faro-blue)] text-white p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div>
          <h4 className="font-display font-bold text-xl">¿Te interesa formarte con nosotros?</h4>
          <p className="text-white/80 text-sm mt-1">Pide información, sin compromiso.</p>
        </div>
        <Link to="/#contacto" data-testid="post-cta" className="btn-pill bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] hover:brightness-95">
          Quiero información
        </Link>
      </div>
    );
  }
  return (
    <div className="mt-12 rounded-2xl bg-[var(--faro-blue)] text-white p-6 sm:p-8" data-testid="post-cta-block">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="max-w-xl">
          <h4 className="font-display font-black text-xl sm:text-2xl leading-tight">{cfg.title}</h4>
          {cfg.subtitle && <p className="text-white/85 text-sm sm:text-base mt-1.5">{cfg.subtitle}</p>}
        </div>
        <Link
          to={cfg.primary.to}
          data-testid={cfg.primary.testid}
          className="btn-pill bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] font-black hover:brightness-95 shrink-0 inline-flex items-center gap-2"
        >
          {cfg.primary.label} <ArrowRight size={18} />
        </Link>
      </div>
      {cfg.secondary && (
        <div className="mt-4 pt-4 border-t border-white/15 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
          <span className="inline-flex items-center gap-2 text-white/85">
            <Calculator size={14} /> ¿Aún no lo tienes claro? La calculadora del enlace principal te dice cuál te toca.
          </span>
          <Link
            to={cfg.secondary.to}
            data-testid={cfg.secondary.testid}
            className="text-[var(--faro-yellow)] font-bold underline underline-offset-2 hover:text-white inline-flex items-center gap-1"
          >
            {cfg.secondary.label} <ArrowRight size={14} />
          </Link>
        </div>
      )}
    </div>
  );
};

export const ArticleBody = ({ post }) => (
  <article className="max-w-3xl mx-auto px-5 sm:px-8 pt-16 pb-24">
    <Link to="/blog" data-testid="back-blog" className="inline-flex items-center gap-1 text-sm font-bold text-[var(--faro-blue)]">
      <ArrowLeft size={14} /> Volver al blog
    </Link>
    <div className="mt-6 flex items-center gap-3 text-xs">
      <span className="uppercase tracking-[0.18em] font-bold text-[var(--faro-red)]">{post.category}</span>
      <span className="inline-flex items-center gap-1 text-[var(--faro-text-soft)]"><Clock size={12} /> {post.read_minutes} min</span>
    </div>
    <h1 className="mt-4 font-display font-black tracking-tighter text-4xl sm:text-5xl leading-tight">{post.title}</h1>
    <p className="mt-4 text-lg text-[var(--faro-text-soft)]">{post.excerpt}</p>
    <div className="mt-8 rounded-2xl overflow-hidden border border-[rgba(30,42,143,0.12)]">
      {post.image && <PictureImg src={post.image} alt={post.title} width={800} height={450} priority sizes="(min-width: 768px) 700px, 100vw" className="w-full h-auto" />}
    </div>
    <div className="mt-8 text-[var(--faro-text)] leading-relaxed space-y-5">
      {Array.isArray(post.blocks) && post.blocks.length ? (
        post.blocks.map((b, i) => <Block key={`b-${i}`} b={b} i={i} />)
      ) : (
        (post.content || "").split(/\n{2,}/).filter(Boolean).map((para, i) => (
          <p key={`${i}-${para.slice(0, 24)}`} className="text-lg whitespace-pre-wrap">{linkify(para)}</p>
        ))
      )}
    </div>

    {/* Extra media: photos of newspapers, TV clips */}
    {Array.isArray(post.media) && post.media.length > 0 && (
      <div className="mt-10 space-y-6" data-testid="post-media">
        <h3 className="font-display font-black text-2xl text-[var(--faro-blue)]">Nos han visto en</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {post.media.map((m, i) => (
            <MediaFigure key={m.url || i} m={m} i={i} />
          ))}
        </div>
      </div>
    )}

    <PostCta post={post} />
  </article>
);
